import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { Bar, Doughnut } from "react-chartjs-2";

import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { motion } from "framer-motion";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBuilding,
  faChartLine,
  faUtensils,
  faClipboardCheck,
  faRankingStar,
  faBook,
  faUserTie,
  faUsers,
  faQrcode,
  faBell,
  faGamepad,
  faBuildingColumns,
  faBrain,
  faShieldAlt,
  faTriangleExclamation,
  faBus,
  faUserGraduate,
  faSchool,
  faPeopleGroup,
  faSitemap,
  faChalkboardUser,
  faClipboardList,
  faNewspaper,
  faCircleInfo,
  faBolt,
  faBookOpen,
  faHandshake,
  faBriefcase,
  faCartShopping,
} from "@fortawesome/free-solid-svg-icons";

import { THEMES } from "./themes/pdThemes";
import { pdTranslations } from "./translation/pd";

import { useOverlayState, PDHeaderAndOverlays } from "./utils/PDHomeHeader";
import DoorModule from "./utils/DoorModule";
import PurpleDoorWarning from "./PurpleDoorWarning";
import PDStoryboard from "./utils/PDStoryboard";

import GrowthLoopAbout from "../growthloop/GrowthLoopAbout";

import PDLeaderboard from "./PDLeaderboard";
import PDStreakHome from "./PDStreakHome";
import PDParent from "./PDParent";
import PDObserver from "./PDObserver";
import PDSubscription from "./PDSubscription";
import PDBOSHelp from "./PDBOSHelp";
import PDIKIGAI from "./PDIKIGAI";
import PDAttentionBOS from "./PDAttentionBOS";
import PDGenZStats from "./utils/PDGenZStats";
import PDAbout from "./PDAbout";
import PurplePowerAgeJourney from "./utils/PurplePowerAgeJourney";
import Anvaya from "./modules/anvaya/Anvaya";
import LifeNHealth from "./modules/lifenhealth/LifeNHealth";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
);

const languages = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "bn", label: "বাংলা" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "as", label: "অসমীয়া" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中文" },
  { code: "ko", label: "한국어" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "fr", label: "Français" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "de", label: "Deutsch" },
  { code: "tr", label: "Türkçe" },
];

const ROLES = [
  "student",
  "parent",
  "mentor",
  "institution",
  "director",
  "influencer",
  "coordinator",
  "merchant",
  "foundher",
  "marketer",
];

const TAB_ICONS = {
  Navigator: "🧭",
  Gifting: "🎁",
  Subscription: "🎟️",
  Streaks: "🔥",
  // GrowthLoop: "🌈",
  AISignals: "🧠",
  People: "🧙",
  Platform: "🟣",
  // PocketMoney: "💰",
};

const TABS = [
  "Navigator",
  "Gifting",
  "Subscription",
  "Streaks",
  // "GrowthLoop",
  "People",
  "AISignals",
  "Platform",
  // "PocketMoney",
];

const NAVIGATOR_TABS = [
  { key: "BOS", label: "BOS", icon: "🚀" },
  { key: "Storyboard", label: "Storyboard", icon: "🦋" },
  { key: "IKIGAI", label: "IKIGAI", icon: "🌟" },
  { key: "AttentionBOS", label: "Attention BOS", icon: "🧠" },
  { key: "GenZStats", label: "DigiGen Stats", icon: "📱" },
  { key: "BOK", label: "BOK", icon: "📚" },
  { key: "DigitalGen", label: "DigitalGen", icon: "⚡" },
  { key: "About", label: "About", icon: "ℹ️" },
];

const PEOPLE_TABS = [
  { key: "Parent", label: "Parent", icon: "👨‍👩‍👧‍👦" },
  { key: "Mentor", label: "Mentor", icon: "🧑‍🏫" },
];

export default function PDAnimation({ msg, onComplete }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pdHomeRef = useRef(null);

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userId = authState._id || null;
  const userCode = authState.think5whyscode || "";
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showAnvaya, setShowAnvaya] = useState(false);
  const [showLifeNHealth, setShowLifeNHealth] = useState(false);
  const [showJourney, setShowJourney] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [theme, setTheme] = useState("DARK");
  const currentTheme = THEMES[theme];
  const overlay = useOverlayState();

  const pdModule = authState?.user?.modules?.find((m) => m.moduleCode === "PD");

  const hasPDAccess = pdModule?.roleCodes?.includes("STUDENT");

  // COMMON THEME HELPERS
  const cardClass = `
    ${currentTheme.card}
    ${currentTheme.text}
    border
    ${currentTheme.border}
    rounded-2xl
    p-5
    shadow-lg
  `;

  const titleClass = `
    text-cyan-200
    text-sm
    lg:text-lg
    font-semibold
  `;

  const paragraphClass = `
    text-gray-100
    text-xs
    lg:text-sm
    leading-relaxed
  `;

  const buttonClass = `
    mt-2
    text-xs
    px-2
    py-1
    rounded-xl
    transition-all
    bg-blue-700
    hover: bg-blue-800
    text-white
  `;

  const [activeTab, setActiveTab] = useState("student");
  const [activeNavTab, setActiveNavTab] = useState("BOS");
  const [activeProfileTab, setActiveProfileTab] = useState("IDSnap");
  const [activePeopleTab, setActivePeopleTab] = useState("Coordinators");

  useEffect(() => {
    const firstAccessibleTab = getDefaultTabForRole(userRole);

    if (firstAccessibleTab && !activeTab) {
      setActiveTab(firstAccessibleTab.id);
    }
  }, []);

  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [userRole, setUserRole] = useState("student");

  const tabs = [
    {
      id: "Coordinators",
      label: "Onboarding",
      icon: faClipboardList,
      roles: [
        "student",
        "parent",
        "mentor",
        "influencer",
        "coordinator",
        "director",
        "institution",
        "foundher",
        "marketer",
        "merchant"
      ],
    },
    {
      id: "Partners",
      label: "Partners",
      icon: faHandshake,
      roles: [
        "student",
        "parent",
        "mentor",
        "influencer",
        "coordinator",
        "director",
        "institution",
        "foundher",
        "marketer",
        "merchant"
      ],
    },
  ];

  const [showPDAbout, setShowPDAbout] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true);
  const [moduleAllowed, setModuleAllowed] = useState(false);

  const hasModuleAccess = (user, moduleCode) => {
    if (!user?.modules || user.modules.length === 0) return false;
    return user.modules.some((m) => m.moduleCode === moduleCode);
  };

  useEffect(() => {
    const moduleCode = "PD";
    const allowed = hasModuleAccess(authState.user, moduleCode);
    if (!allowed) {
      setModuleAllowed(false);
      setActiveTab("onboarding");
    } else {
      setModuleAllowed(true); // Allowed to access
    }
  }, [authState]);

  const t = (key) =>
    pdTranslations[selectedLanguage]?.[key] ?? pdTranslations.en?.[key] ?? key;

  const getDefaultTabForRole = (role) => {
    return tabs.find(
      (tab) => tab.roles.includes(role) && canAccessTab(tab.roles),
    );
  };

  const handleRoleChange = (role) => {
    setUserRole(role);
    const matchedTab = getDefaultTabForRole(role);
    if (matchedTab) {
      setActiveTab(matchedTab.id);
    }
  };

  const allowedRoles = useMemo(() => {
    if (!authState?.user?.modules) return [];
    return authState.user.modules.flatMap((m) => m.roleCodes || []);
  }, [authState]);

  const canAccessTab = (tabRoles = []) => {
    if (!tabRoles?.length) return false;
    return tabRoles.some((role) => allowedRoles.includes(role.toUpperCase()));
  };

  const [open, setOpen] = useState(false);
  const [doorMessage, setDoorMessage] = useState("");
  const [doorStatus, setDoorStatus] = useState("idle"); // idle | denied | verified | welcome

  const handleDoorClick = () => {
    if (!hasPDAccess) {
      setDoorStatus("denied");
      setDoorMessage("🎟️ Boarding Pass Required");
      return; // STOP HERE
    }
    setDoorStatus("verified");
    setDoorMessage("🚀 Boarding Pass Verified");
    setTimeout(() => {
      setOpen(true);
      setTimeout(() => {
        setDoorStatus("welcome");
        setDoorMessage(`${authState?.user?.userName || "Explorer"}`);
      }, 800);
    }, 900);
  };

  return (
    <div className="flex flex-col min-h-[85vh] w-full max-w-full overflow-x-hidden mt-2">
      <PDHeaderAndOverlays
        {...overlay}
        userName={userName}
        t={t}
        userRole={userRole}
        currentTheme={currentTheme}
        handleLogout={handleLogout}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        handleRoleChange={handleRoleChange}
        ROLES={ROLES}
        theme={theme}
        setTheme={setTheme}
        THEMES={THEMES}
        languages={languages}
        authState={authState}
        moduleAllowed={moduleAllowed}
      />

      <div className="flex flex-col justify-center items-center gap-2">
        {!moduleAllowed && (
          <div className="flex flex-col">
            <PurpleDoorWarning
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              t={t}
              role={userRole}
              currentTheme={currentTheme}
              isNew={true}
            />
            <button
              onClick={() => setShowPDAbout((prev) => !prev)}
              className="mb-2 px-3 py-1 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              {showPDAbout ? "Hide About PurpleDoor" : "Show About PurpleDoor"}
            </button>
          </div>
        )}
        {showPDAbout && (
          <PDAbout t={t} role={userRole} currentTheme={currentTheme} />
        )}
      </div>

      {overlay.showPrimeComponents && (
        <>
          {moduleAllowed && (
            <>
              <div className="border-b-2 border-cyan-200 rounded-2xl px-2 py-1 mt-2">
                <div className="flex flex-wrap justify-center gap-2 w-full">
                  <button
                    onClick={() => navigate("/purpledoor/ids")}
                    className="group relative overflow-hidden rounded-xl border border-cyan-400/30 bg-gradient-to-r from-cyan-500/15 via-sky-500/10 to-teal-500/15 px-1 py-1.5 backdrop-blur-md transition-all duration-300 hover:scale-[1.02] hover:border-cyan-300 hover:shadow-[0_0_16px_rgba(34,211,238,0.35)] active:scale-95"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-cyan-300/20 to-cyan-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>

                    <span className="relative flex items-center gap-1.5 font-semibold text-cyan-700 group-hover:text-cyan-300 transition-colors duration-300">
                      🎥
                      <span className="text-[11px] lg:text-xs whitespace-nowrap">
                        Create Content.Earn on YouTube.
                      </span>
                      <span className="text-cyan-600 group-hover:translate-x-0.5 transition-transform text-[11px]">
                        →
                      </span>
                    </span>
                  </button>

                  <button
                    onClick={() => setShowJourney(!showJourney)}
                    className="group relative overflow-hidden rounded-xl border border-fuchsia-400/30 bg-gradient-to-r from-fuchsia-500/15 via-pink-500/10 to-rose-500/15 px-3 py-1.5 backdrop-blur-md transition-all duration-300 hover:scale-[1.02] hover:border-pink-300 hover:shadow-[0_0_16px_rgba(236,72,153,0.35)] active:scale-95"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-pink-300/20 to-pink-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>

                    <span className="relative flex items-center gap-1.5 font-medium text-pink-400 group-hover:text-pink-100 transition-colors duration-300">
                      💜
                      <span className="text-[11px] lg:text-xs whitespace-nowrap">
                        PurpleDoor Journey
                      </span>
                      <span
                        className={`text-[10px] transition-transform duration-300 ${
                          showJourney ? "rotate-180" : ""
                        }`}
                      >
                        ⮟
                      </span>
                    </span>
                  </button>
                </div>

                {showJourney && (
                  <div className="mt-4">
                    <PurplePowerAgeJourney />
                  </div>
                )}
                <div className="border-y-2 border-dashed border-purple-400 rounded-3xl py-2">
                  <div className="flex flex-row flex-wrap justify-center items-stretch gap-1 w-full max-w-full px-1">
                    {/* <DoorModule
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/digitalboutiquedashboard")}
                      soundSrc="/assets/door-open-digib.wav"
                      frameClass="border-pink-500/60"
                      roomGradientClass="from-zinc-950 via-black to-zinc-900"
                      roomTextClass="text-cyan-300"
                      roomLabel="✨ Digital Boutique"
                      doorBgClass="bg-gradient-to-b from-purple-500 via-purple-700 to-purple-900"
                      doorGradientClass="from-pink-500 via-purple-600 to-cyan-500"
                      doorIcon="🛍️"
                      doorLabel="DigiBoutique"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.7)]"
                      glowClass="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-400"
                    /> */}
                    <DoorModule
                      shape="pill"
                      sizeClass="w-26 h-10 xs:w-32 xs:h-11 sm:w-32 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/digitalboutiquedashboard")}
                      soundSrc="/assets/door-open-digib.wav"
                      soundVolume={0.6}
                      clickDelay={120}
                      frameClass="border-yellow-200"
                      frameExtraClass="shadow-lg shadow-cyan-800/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="✨"
                      pillLine2="DigiBoutique"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="Digital Boutique"
                    />
                    <DoorModule
                      shape="pill"
                      // sizeClass="w-36 h-10 xs:w-40 xs:h-11 sm:w-42 sm:h-12"
                      sizeClass="w-28 h-10 xs:w-32 xs:h-11 sm:w-32 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/foundher")}
                      soundSrc="/assets/door-open-fh.wav"
                      frameClass="border-pink-500"
                      frameExtraClass="shadow-lg shadow-pink-500"
                      doorBgClass="bg-zinc-800"
                      pillLine1="🌸"
                      pillLine2="FoundHer"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="Young Women Enterprenurer Activated"
                    />
                    <DoorModule
                      shape="pill"
                      // sizeClass="w-40 h-10 xs:w-44 xs:h-11 sm:w-48 sm:h-12"
                      // sizeClass="w-36 h-10 xs:w-40 xs:h-11 sm:w-42 sm:h-12"
                      sizeClass="w-28 h-10 xs:w-32 xs:h-11 sm:w-32 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/finditlocal")}
                      soundSrc="/assets/door-open-f.wav"
                      frameClass="border-teal-500"
                      frameExtraClass="shadow-lg shadow-teal-500/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="📍"
                      pillLine2="FindItLocal"
                      handleClass="bg-white"
                      glowClass="bg-purple-400"
                      glowVariant="single"
                      welcomeIcon="Mobile Powered Walkin"
                    />
                    
                  </div>
                  {/* <div className="flex flex-col items-center w-full px-1 space-y-1"> */}

                  <div className="flex flex-row flex-wrap justify-center gap-1 w-full max-w-full px-1">
                    {/* <div className="flex flex-row flex-wrap justify-center gap-1 w-full max-w-full px-1"> */}
                    <DoorModule
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onDoorClick={handleDoorClick}
                      onEnter={() => navigate("/purpledoordashboard")}
                      soundSrc="/assets/door-open-pd.wav"
                      frameClass="border-purple-500"
                      roomGradientClass="from-purple-900 via-purple-950 to-black"
                      roomTextClass="text-green-300"
                      roomLabel="✨ PurpleDoor World"
                      doorBgClass="bg-gradient-to-b from-purple-500 via-purple-700 to-purple-900"
                      doorGradientClass="from-purple-500 via-purple-700 to-purple-900"
                      doorIcon="🟣"
                      doorLabel="PurpleDoor"
                      handleClass="bg-white"
                      glowClass="bg-purple-400"
                    />
                    <DoorModule
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/lifereplaydashboard")}
                      soundSrc="/assets/door-open-lr.wav"
                      frameClass="border-indigo-500"
                      roomGradientClass="from-indigo-950 via-black to-black"
                      roomTextClass="text-indigo-200"
                      roomLabel="🌍 LifeReplays"
                      doorBgClass="bg-gradient-to-b from-purple-500 via-purple-700 to-purple-900"
                      doorGradientClass="from-indigo-500 via-violet-700 to-indigo-950"
                      doorIcon="🌍"
                      doorLabel="LifeReplays"
                      handleClass="bg-white"
                      glowClass="bg-purple-400"
                    />
                    {/* </div>
                      <div className="flex flex-row flex-wrap justify-center gap-1 w-full max-w-full px-1">  */}
                    <DoorModule
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onDoorClick={handleDoorClick}
                      onEnter={() => navigate("/pulsedashboard")}
                      soundSrc="/assets/door-open-pulse.wav"
                      frameClass="border-cyan-400/60"
                      roomGradientClass="from-black via-slate-950 to-black"
                      roomTextClass="text-emerald-300"
                      roomLabel="📡 Daily Pulse Signal"
                      doorBgClass="bg-gradient-to-b from-purple-500 via-purple-700 to-purple-900"
                      doorGradientClass="from-cyan-400 via-blue-500 to-indigo-600"
                      doorIcon="📊"
                      doorLabel="Pulse"
                      handleClass="bg-emerald-300 shadow-[0_0_10px_rgba(52,211,153,0.8)]"
                      glowClass="bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500"
                      welcomeIcon="📡"
                    />
                    <DoorModule
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/scoreplus")}
                      soundSrc="/assets/door-open-digib.wav"
                      frameClass="border-pink-500/60"
                      roomGradientClass="from-zinc-950 via-black to-zinc-900"
                      roomTextClass="text-cyan-300"
                      roomLabel="💜 ScorePlus"
                      doorBgClass="bg-gradient-to-b from-purple-500 via-purple-700 to-purple-900"
                      doorGradientClass="from-pink-500 via-purple-600 to-cyan-500"
                      doorIcon="💜"
                      doorLabel="ScorePlus"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.7)]"
                      glowClass="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-400"
                    />
                  </div>
                  <div className="flex flex-row flex-wrap justify-center items-center gap-1 w-full max-w-full px-1">
                    <DoorModule
                      shape="pill"
                      sizeClass="w-26 h-10 xs:w-32 xs:h-11 sm:w-32 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/growthloop")}
                      soundSrc="/assets/door-open-reu.wav"
                      soundVolume={0.6}
                      clickDelay={120}
                      frameClass="border-yellow-200"
                      frameExtraClass="shadow-lg shadow-cyan-800/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="🧒"
                      pillLine2="GrowthLoop"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="Child Development"
                    />
                    <DoorModule
                      shape="pill"
                      sizeClass="w-28 h-10 xs:w-32 xs:h-11 sm:w-32 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => {
                        setActiveTab("Anvaya");
                        setShowAnvaya(true);
                      }}
                      // onEnter={() => navigate("/anvaya")}
                      soundSrc="/assets/door-open-reu.wav"
                      soundVolume={0.6}
                      clickDelay={120}
                      frameClass="border-yellow-200"
                      frameExtraClass="shadow-lg shadow-cyan-800/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="🤰"
                      pillLine2="Anvaya"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="Being with child"
                    />
                    <DoorModule
                      shape="pill"
                      sizeClass="w-26 h-10 xs:w-32 xs:h-11 sm:w-32 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      // onEnter={() => navigate("")}
                      onEnter={() => {
                        setActiveTab("LifeNHealth");
                        setShowLifeNHealth(true);
                      }}
                      // onEnter={() => navigate("/lifenhealth")}
                      soundSrc="/assets/door-open-reu.wav"
                      soundVolume={0.6}
                      clickDelay={120}
                      frameClass="border-yellow-200"
                      frameExtraClass="shadow-lg shadow-cyan-800/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="🧬"
                      pillLine2="LifeNHealth"
                      // pillLine2="🩺HealthCompanions"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="LifeNHealth"
                    />
                    {/* <DoorModule
                      shape="pill"
                      sizeClass="w-26 h-10 xs:w-32 xs:h-11 sm:w-32 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => {
                        setActiveTab("Gifting");
                        setShowSubscription(true);
                      }}
                      soundSrc="/assets/door-open-reu.wav"
                      soundVolume={0.6}
                      clickDelay={120}
                      frameClass="border-yellow-200"
                      frameExtraClass="shadow-lg shadow-cyan-800/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="🎁🎟️"
                      pillLine2="Gift/Subscribe"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="Gifting & Subscription"
                    /> */}
                  </div>
                  {showAnvaya && activeTab === "Anvaya" && (
                    <div className="mt-4 bg-black/70 flex items-center justify-center border-y-2 border-blue-600 rounded-xl p-2">
                      <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                        <button
                          onClick={() => setActiveTab(null)}
                          className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                        >
                          ✕
                        </button>

                        <Anvaya />
                      </div>
                    </div>
                  )}
                   {showLifeNHealth && activeTab === "LifeNHealth" && (
                    <div className="mt-4 bg-black/70 flex items-center justify-center border-y-2 border-blue-600 rounded-xl p-2">
                      <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                        <button
                          onClick={() => setActiveTab(null)}
                          className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                        >
                          ✕
                        </button>

                        <LifeNHealth />
                      </div>
                    </div>
                  )}
                  {/* {showSubscription && activeTab === "Gifting" && (
                    <div className="mt-4 bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                      <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                        <button
                          onClick={() => setActiveTab(null)}
                          className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                        >
                          ✕
                        </button>
                        <PDSubscription
                          t={t}
                          role={userRole}
                          currentTheme={currentTheme}
                        />
                      </div>
                    </div>
                  )} */}

                  <div className="flex flex-row flex-wrap justify-center items-stretch gap-1 w-full max-w-full px-1">
                    <DoorModule
                      shape="pill"
                      sizeClass="w-40 h-10 xs:w-44 xs:h-11 sm:w-48 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/hustle")}
                      soundSrc="/assets/door-open-h.wav"
                      frameClass="border-amber-500"
                      frameExtraClass="shadow-lg shadow-amber-500/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="💼"
                      pillLine2="Hustles"
                      // pillLine2="Youth Work"
                      handleClass="bg-white"
                      glowClass="bg-purple-400"
                      glowVariant="single"
                    />
                    <DoorModule
                      shape="pill"
                      sizeClass="w-40 h-10 xs:w-44 xs:h-11 sm:w-48 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/reunion")}
                      soundSrc="/assets/door-open-reu.wav"
                      soundVolume={0.6}
                      clickDelay={120}
                      frameClass="border-yellow-200"
                      frameExtraClass="shadow-lg shadow-cyan-800/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="🔄"
                      pillLine2="Reunion"
                      // pillLine2="Gather. Celebrate. Compete."
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="Collaborative Fun"
                    />
                  </div>
                  <div className="flex flex-row flex-wrap justify-center items-stretch gap-1 w-full max-w-full px-1">
                    <DoorModule
                      shape="pill"
                      sizeClass="w-40 h-10 xs:w-44 xs:h-11 sm:w-48 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/vocalforlocal")}
                      soundSrc="/assets/door-open-vfl.wav"
                      frameClass="border-amber-800"
                      frameExtraClass="shadow-lg shadow-orange-500/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="🗣️"
                      pillLine2="VocalForLocal"
                      // pillLine2="Shop real. Support local."
                      handleClass="bg-white"
                      glowClass="bg-orange-400"
                      glowVariant="single"
                    />
                    <DoorModule
                      shape="pill"
                      sizeClass="w-40 h-10 xs:w-44 xs:h-11 sm:w-48 sm:h-12"
                      open={open}
                      doorStatus={doorStatus}
                      authState={authState}
                      onEnter={() => navigate("/nabora")}
                      soundSrc="/assets/door-open-nab.wav"
                      soundVolume={0.6}
                      clickDelay={120}
                      frameClass="border-cyan-800"
                      frameExtraClass="shadow-lg shadow-cyan-800/40"
                      doorBgClass="bg-zinc-800"
                      pillLine1="💜"
                      pillLine2="NABORA"
                      // pillLine2="Local DigiFair"
                      handleClass="bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                      glowClass="bg-violet-500/30"
                      glowVariant="double"
                      welcomeSubLabel="Local DigiFair Activated"
                    />
                  </div>
                </div>
                <div className="flex justify-center w-full px-1 items-center mt-2 mb-2 border-t-2 border-dotted rounded-3xl border-red-300">
                  <img
                    src="/assets/filtvbanner.webp"
                    alt="FiLTV"
                    className="block w-2/3 lg:w-1/2 h-auto max-w-full rounded-lg cursor-pointer hover:opacity-95 transition object-contain"
                    onClick={() => navigate("/filtv")}
                  />
                </div>
              </div>

              <div className="w-full px-2 min-[390px]:px-3 lg:px-8 py-2 grid grid-cols-3 lg:grid-cols-5 gap-2 min-[390px]:gap-3 lg:gap-3 mt-2">
                {TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);

                      setTimeout(() => {
                        pdHomeRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 100);
                    }}
                    className={`
                  w-full
                  min-h-[32px]
                  min-[390px]:min-h-[40px]
                  px-1
                  min-[390px]:px-2
                  py-2
                  flex flex-col items-center justify-center
                  rounded-md
                  border
                  transition-all duration-200
                  hover:text-white
                  ${
                    activeTab === tab
                      ? currentTheme.tabActive
                      : `${currentTheme.tabInactive} ${currentTheme.tabHover}`
                  }
                `}
                  >
                    <div className="flex flex-col items-center leading-none">
                      <p className="text-xs leading-none">{TAB_ICONS[tab]}</p>
                      <p className="text-[11px] lg:text-xs text-white leading-none">
                        {tab}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* CONTENT SECTION */}
              <div ref={pdHomeRef} className="flex-1 relative z-10">
                {activeTab === "Navigator" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <div className="mt-3 p-3 rounded-xl border border-cyan-500/30 bg-slate-900/90">
                        <div className="grid grid-cols-2 lg:grid-cols-7 gap-1">
                          {NAVIGATOR_TABS.map((tab) => (
                            <button
                              key={tab.key}
                              onClick={() => setActiveNavTab(tab.key)}
                              className={`px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 flex items-center justify-center gap-2
                            ${
                              activeNavTab === tab.key
                                ? "bg-cyan-600 text-slate-900 shadow-md"
                                : "bg-slate-800 text-cyan-200 hover:bg-slate-700 hover:text-white"
                            }`}
                            >
                              <span className="text-xs">{tab.icon}</span>
                              <span className="text-xs">{tab.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      {activeNavTab === "BOS" && (
                        <PDBOSHelp setActiveTab={setActiveTab} />
                      )}

                      {activeNavTab === "Storyboard" && <PDStoryboard />}

                      {activeNavTab === "IKIGAI" && (
                        <PDIKIGAI setActiveTab={setActiveTab} />
                      )}

                      {activeNavTab === "AttentionBOS" && (
                        <PDAttentionBOS setActiveTab={setActiveTab} />
                      )}

                      {activeNavTab === "GenZStats" && (
                        <PDGenZStats
                          selectedLanguage={selectedLanguage}
                          role={userRole}
                          currentTheme={currentTheme}
                        />
                      )}

                      {activeNavTab === "BOK" && (
                        <div className="space-y-6 text-center bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
                          <h2 className={titleClass}>GenZ+ BOK</h2>
                          <button
                            onClick={() => navigate("/lnd")}
                            disabled
                            className={buttonClass}
                          >
                            📘 Open BOK Learning Ecosystem
                          </button>
                          <div className="space-y-3 mt-3">
                            <p className={paragraphClass}>
                              ⚡ BOK (Body Of Knowledge) is your launchpad
                              beyond classrooms, random internet content, and
                              passive GenAI consumption — built for Gen Z, Gen
                              Alpha, and future digital creators, operators, and
                              transformation leaders.
                            </p>
                            <p className={paragraphClass}>
                              🌍 This is where knowledge transforms into
                              real-world capability, digital confidence,
                              ecosystem thinking, certifications, earning
                              opportunities, and startup journeys.
                            </p>
                            <p className={paragraphClass}>
                              🧠 Learn practical process orientation, AI,
                              automation, data-centric thinking, digital
                              operations, and real-world ecosystem skills
                              through immersive learning and smart assessments.
                            </p>
                            <p className={paragraphClass}>
                              🎯 Build certifications, practical capabilities,
                              behavioral discipline, and job-ready expertise
                              that supports modern careers, freelancing,
                              entrepreneurship, and ecosystem opportunities.
                            </p>
                            <p className={paragraphClass}>
                              🚀 Start your own certified Digital Boutique
                              Advisor journey individually or with friends in
                              your geographical area and grow as a
                              transformation partner within the Think5Whys
                              ecosystem.
                            </p>
                            <p className={paragraphClass}>
                              📈 Track your learning progress, certifications,
                              assessments, growth pathways, achievements, and
                              continuous transformation journey from knowledge
                              to earning.
                            </p>
                          </div>
                        </div>
                      )}

                      {activeNavTab === "DigitalGen" && (
                        <div className="space-y-6 text-center bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
                          <h2 className={titleClass}>
                            Digital Generation (Gen Z, Gen Alpha)
                          </h2>
                          <button
                            onClick={() => navigate("/ninetysix")}
                            className={buttonClass}
                          >
                            ⚡ Know More
                          </button>
                          <div className="space-y-3 mt-3">
                            <p className={paragraphClass}>
                              🚀 Built for creators, learners, future founders,
                              and digital-first generations navigating an
                              AI-powered world.
                            </p>

                            <p className={paragraphClass}>
                              ⚡ Learn practical skills, use AI productively,
                              join execution teams, and explore startup and
                              earning opportunities.
                            </p>

                            <p className={paragraphClass}>
                              📈 Progress through continuous learning,
                              capability building, smart certifications, and
                              measurable growth journeys.
                            </p>

                            <p className={paragraphClass}>
                              🧠 Develop stronger habits, better
                              decision-making, and Kaizen-based improvement
                              through a transformation-focused ecosystem.
                            </p>
                          </div>
                        </div>
                      )}

                      {activeNavTab === "About" && (
                        <PDAbout
                          t={t}
                          role={userRole}
                          currentTheme={currentTheme}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* People Group */}
                {activeTab === "People" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <div className="mt-3 p-3 rounded-xl border border-cyan-500/30 bg-slate-900/90">
                        <div className="grid grid-cols-3 lg:grid-cols-7 gap-1">
                          {PEOPLE_TABS.map((tab) => (
                            <button
                              key={tab.key}
                              onClick={() => setActivePeopleTab(tab.key)}
                              className={`px-1 py-1 rounded-lg text-xs font-medium transition-all duration-200 flex items-center justify-center gap-2
                              ${
                                activePeopleTab === tab.key
                                  ? "bg-cyan-500 text-slate-900 shadow-md"
                                  : "bg-slate-800 text-cyan-200 hover:bg-slate-700 hover:text-white"
                              }`}
                            >
                              <span className="text-xs">{tab.icon}</span>
                              <span className="text-xs">{tab.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {activePeopleTab === "Parent" && (
                        <PDParent
                          t={t}
                          role={userRole}
                          currentTheme={currentTheme}
                        />
                      )}

                      {activePeopleTab === "Mentor" && (
                        <PDObserver
                          t={t}
                          role={userRole}
                          currentTheme={currentTheme}
                        />
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "GrowthLoop" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <h2 className={titleClass}>
                        🌈 Kids & Children GrowthLoop
                      </h2>
                      <button
                        onClick={() => navigate("/growthloop")}
                        className={buttonClass}
                      >
                        🌈 Explore GrowthLoop
                      </button>
                      <GrowthLoopAbout />
                    </div>
                  </div>
                )}

                {activeTab === "AISignals" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <h2 className={titleClass}>AI Signals</h2>
                      <button
                        disabled={true}
                        onClick={() => navigate("/signals")}
                        className={buttonClass}
                      >
                        🧠 Explore AI Signals
                      </button>

                      <div className="space-y-3 mt-3">
                        <p className={paragraphClass}>
                          AI Signals provides cognitive analysis based on the
                          information, activities, assessments, and inputs you
                          share within the OpenDoor ecosystem.
                        </p>

                        <p className={paragraphClass}>
                          By identifying patterns, behaviors, strengths,
                          interests, and growth opportunities, AI Signals helps
                          transform raw data into meaningful personal insights.
                        </p>

                        <p className={paragraphClass}>
                          More than analytics, AI Signals acts as a personal
                          transformation companion, helping you understand where
                          you are today and where you can grow tomorrow.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "Streaks" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <PDStreakHome role={userRole} />
                    </div>
                  </div>
                )}

                {activeTab === "Leaderboard" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <PDLeaderboard
                        t={t}
                        role={userRole}
                        currentTheme={currentTheme}
                      />
                    </div>
                  </div>
                )}

                {activeTab === "Platform" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <h2 className={titleClass}>Think5Whys Platform</h2>
                      <button
                        onClick={() => navigate("/dashboard")}
                        className={buttonClass}
                      >
                        Explore Think5Whys Platform
                      </button>
                      <div className="space-y-3 mt-3">
                        <p className={paragraphClass}>
                          Think5Whys transforms learning into action by helping
                          young people build skills, complete real projects, and
                          create earning opportunities.
                        </p>

                        <p className={paragraphClass}>
                          Learn by doing, collaborate in small execution teams,
                          use AI as a productivity tool, and turn ideas into
                          measurable outcomes.
                        </p>

                        <p className={paragraphClass}>
                          More than a learning platform, Think5Whys is a
                          transformation ecosystem for creators, operators, and
                          future startup builders.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "Subscription" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <PDSubscription
                        t={t}
                        role={userRole}
                        currentTheme={currentTheme}
                      />
                    </div>
                  </div>
                )}

                {activeTab === "Gifting" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <PDSubscription
                        t={t}
                        role={userRole}
                        currentTheme={currentTheme}
                      />
                    </div>
                  </div>
                )}

                {activeTab === "PocketMoney" && (
                  <div className="bg-black/70 flex items-center justify-center border-t-2 border-cyan-200 rounded-xl p-2">
                    <div className="relative w-full bg-gray-900/80 p-4 space-y-5">
                      <button
                        onClick={() => setActiveTab(null)}
                        className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
                      >
                        ✕
                      </button>
                      <h2 className={titleClass}>PocketMoney</h2>
                      <div className="space-y-3 mt-3">
                        <p className={paragraphClass}>
                          Pocket earning activities encourage financial
                          awareness and productivity. Enroll as Digital
                          Franchise (Director / Coordinators) to earn Pocket
                          Money / Any Hustles.
                        </p>
                        <p className={paragraphClass}>
                          See Full Details in the "🚀 GenZ / Next Gen Digital
                          Franchise" Section
                        </p>
                        <p className={paragraphClass}>
                          More opportunities to earn pocket money for all will
                          be unveiled soon. Stay Tuned!!
                        </p>
                      </div>

                      <button
                        onClick={() => navigate("/bookshelf")}
                        disabled
                        className={buttonClass}
                      >
                        Explore Safe Earnings Space
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
